import {
  Alert,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { ENTITY_CATEGORIES, categoryOf } from "../../utils/entity/categories";
import { useLocalState } from "../../utils/hooks/global/useLocalStorage";
import CustomButton from "../global/CustomButton";

// Edits an entity that is not an exchange: name, logo, category and flags
const EditWalletForm = ({ entity, onSubmit }) => {
  const [jwt] = useLocalState(null, "jwt");
  const [formData, setFormData] = useState({
    name: entity.name ?? "",
    logo: entity.logo ?? "",
    category: categoryOf(entity),
    hasNativeAssets: !!entity.hasNativeAssets,
    hasStaking: !!entity.hasStaking,
  });
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const setField = (name, value) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const handleSave = async () => {
    setSaving(true);
    setErrorMessage("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/entities/${entity.baseEntity.id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + jwt,
          },
          body: JSON.stringify({
            type: "wallet",
            wallet: { ...formData, name: formData.name.trim() },
          }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message = Array.isArray(data.message)
          ? data.message.join(", ")
          : data.message;
        throw new Error(message || `Request failed (${response.status})`);
      }
      onSubmit();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Edit {entity.name}
      </Typography>
      <TextField
        label="Name"
        value={formData.name}
        onChange={(e) => setField("name", e.target.value)}
        required
        fullWidth
      />
      <FormControl fullWidth>
        <InputLabel id="entity-category-label">Category</InputLabel>
        <Select
          labelId="entity-category-label"
          label="Category"
          value={formData.category}
          onChange={(e) => setField("category", e.target.value)}
        >
          {ENTITY_CATEGORIES.map((category) => (
            <MenuItem key={category.value} value={category.value}>
              {category.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Logo URL"
        value={formData.logo}
        onChange={(e) => setField("logo", e.target.value)}
        fullWidth
      />
      <Stack direction="row" spacing={3} flexWrap="wrap">
        <FormControlLabel
          control={
            <Switch
              checked={formData.hasNativeAssets}
              onChange={(e) => setField("hasNativeAssets", e.target.checked)}
            />
          }
          label="Native assets"
        />
        <FormControlLabel
          control={
            <Switch
              checked={formData.hasStaking}
              onChange={(e) => setField("hasStaking", e.target.checked)}
            />
          }
          label="Staking"
        />
      </Stack>
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      <Stack direction="row" justifyContent="flex-end">
        <CustomButton
          buttonText={saving ? "Saving…" : "Save"}
          color="black"
          disabled={saving || !formData.name.trim()}
          onClick={handleSave}
        />
      </Stack>
    </Stack>
  );
};

export default EditWalletForm;
